import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { useProvider } from '../../contexts/ProviderContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import ProviderLayout from './ProviderLayout';

interface NewVariant {
  name: string;
  value: string;
  sku: string;
  price: number;
  quantity: number;
}

const NewProduct: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentProviderId } = useProvider();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    vendor: '',
    category: '',
    productType: '',
    photoUrl: '',
    tags: [] as string[],
    collections: [] as string[],
    sku: '',
  });

  const [variants, setVariants] = useState<NewVariant[]>([
    {
      name: 'Size',
      value: '',
      sku: '',
      price: 0,
      quantity: 0,
    },
  ]);

  const [newTag, setNewTag] = useState('');
  const [newCollection, setNewCollection] = useState('');

  // Check if user has authorization to create products for this provider
  useEffect(() => {
    const checkAuthorization = async () => {
      if (!user?.id || !currentProviderId) {
        setCheckingAuth(false);
        return;
      }

      try {
        // Check if user has auth_users_providers entry for this provider
        const { data, error } = await supabase
          .from('auth_users_providers')
          .select('provider_id')
          .eq('auth_user_id', user.id)
          .eq('provider_id', currentProviderId)
          .single();

        if (error || !data) {
          // No authorization - redirect back to catalog
          setUnauthorized(true);
          toast.error('You do not have permission to add products for this provider.');
          setTimeout(() => {
            navigate('/provider/catalog');
          }, 2000);
        }
      } catch (err) {
        console.error('Error checking authorization:', err);
        setUnauthorized(true);
        toast.error('Failed to verify permissions. Redirecting...');
        setTimeout(() => {
          navigate('/provider/catalog');
        }, 2000);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuthorization();
  }, [user?.id, currentProviderId, navigate]);

  // Handle form field changes
  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle tag management
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      updateField('tags', [...formData.tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    updateField('tags', formData.tags.filter(t => t !== tag));
  };

  // Handle collection management
  const addCollection = () => {
    if (newCollection.trim() && !formData.collections.includes(newCollection.trim())) {
      updateField('collections', [...formData.collections, newCollection.trim()]);
      setNewCollection('');
    }
  };

  const removeCollection = (collection: string) => {
    updateField('collections', formData.collections.filter(c => c !== collection));
  };

  // Handle variant changes
  const updateVariant = (index: number, field: keyof NewVariant, value: any) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  const addVariant = () => {
    setVariants([
      ...variants,
      {
        name: 'Size',
        value: '',
        sku: '',
        price: 0,
        quantity: 0,
      },
    ]);
  };

  const deleteVariant = (index: number) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  // Create product
  const handleCreate = async () => {
    try {
      setSaving(true);

      // Validation
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!currentProviderId) {
        throw new Error('No provider selected');
      }
      if (variants.length === 0) {
        throw new Error('At least one variant is required');
      }
      for (const variant of variants) {
        if (!variant.value || !variant.value.trim()) {
          throw new Error('All variants must have a value');
        }
        if (variant.price < 0) {
          throw new Error('Price cannot be negative');
        }
        if (variant.quantity < 0) {
          throw new Error('Quantity cannot be negative');
        }
      }

      // Step 1: Create product_description
      const { data: productData, error: productError } = await supabase
        .from('product_descriptions')
        .insert({
          provider_id: currentProviderId,
          title: formData.title,
          description: formData.description,
          vendor: formData.vendor,
          category: formData.category,
          product_type: formData.productType,
          photo_url: formData.photoUrl,
          tags: formData.tags,
          collections: formData.collections,
          sku: formData.sku || `PROD-${Date.now()}`,
        })
        .select()
        .single();

      if (productError) throw productError;

      // Step 2: Create variants and inventory
      for (const variant of variants) {
        // Create variant
        const { data: variantData, error: variantError } = await supabase
          .from('product_variants')
          .insert({
            product_description_id: productData.id,
            provider_id: currentProviderId,
            sku: variant.sku || formData.sku || productData.sku,
            variant_name: variant.name,
            variant_value: variant.value,
          })
          .select()
          .single();

        if (variantError) throw variantError;

        // Create inventory
        const { error: inventoryError } = await supabase
          .from('product_inventory')
          .insert({
            product_variant_id: variantData.id,
            provider_id: currentProviderId,
            quantity: variant.quantity,
            price: variant.price,
          });

        if (inventoryError) throw inventoryError;
      }

      // Success - redirect to catalog
      navigate('/provider/catalog');
    } catch (e: any) {
      console.error('Error creating product:', e);
      toast.error(e.message || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  // Show loading while checking authorization
  if (checkingAuth) {
    return (
      <ProviderLayout backTo="/provider/catalog">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-xl rounded-lg p-12 text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/50 ">Verifying permissions...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  // Show nothing if unauthorized (toast already shown, redirect in progress)
  if (unauthorized) {
    return null;
  }

  return (
    <ProviderLayout backTo="/provider/catalog">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white ">Create New Product</h1>
        </div>

        {/* Form */}
        <div className="glass-card rounded-xl rounded-lg p-6 space-y-6">
          {/* Product Information Section */}
          <div className="border-b border-white/[0.08] pb-6">
            <h2 className="text-lg font-semibold text-white  mb-4">Product Information</h2>

            {/* Photo Preview */}
            {formData.photoUrl && (
              <div className="flex justify-center mb-4">
                <img
                  src={formData.photoUrl}
                  alt="Product preview"
                  className="w-48 h-48 object-cover rounded border border-white/15"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/192x192?text=Invalid+URL';
                  }}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="e.g., Nike Air Max 90"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="Product description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Vendor
                </label>
                <input
                  type="text"
                  value={formData.vendor}
                  onChange={(e) => updateField('vendor', e.target.value)}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="e.g., Nike"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="e.g., Sneakers"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Product Type
                </label>
                <input
                  type="text"
                  value={formData.productType}
                  onChange={(e) => updateField('productType', e.target.value)}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="e.g., Footwear"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Base SKU
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => updateField('sku', e.target.value)}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="Auto-generated if empty"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Photo URL
                </label>
                <input
                  type="text"
                  value={formData.photoUrl}
                  onChange={(e) => updateField('photoUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Tags */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-white/70  mb-1">
                Tags
              </label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/15 text-orange-500 rounded  text-sm"
                  >
                    {tag}
                    <button onClick={() => removeTag(tag)} className="text-primary-900 hover:text-orange-400">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag..."
                  className="flex-1 px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                />
                <button
                  onClick={addTag}
                  className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm hover:bg-orange-400 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Collections */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-white/70  mb-1">
                Collections
              </label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {formData.collections.map((collection) => (
                  <span
                    key={collection}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-transparent text-white/70 rounded  text-sm"
                  >
                    {collection}
                    <button onClick={() => removeCollection(collection)} className="text-white hover:text-white/50">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCollection}
                  onChange={(e) => setNewCollection(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCollection())}
                  placeholder="Add collection..."
                  className="flex-1 px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                />
                <button
                  onClick={addCollection}
                  className="px-4 py-2 bg-gray-600 text-white rounded  text-sm hover:bg-gray-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Variants Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white ">Variants *</h2>
              <button
                onClick={addVariant}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm hover:bg-orange-400 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Variant
              </button>
            </div>

            <div className="space-y-3">
              {variants.map((variant, index) => (
                <div key={index} className="border border-white/15 rounded p-4 bg-transparent">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs text-white/50  mb-1">Name</label>
                      <input
                        type="text"
                        value={variant.name}
                        onChange={(e) => updateVariant(index, 'name', e.target.value)}
                        className="w-full px-2 py-1 border border-white/15 rounded  text-sm"
                        placeholder="Size"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50  mb-1">Value *</label>
                      <input
                        type="text"
                        value={variant.value}
                        onChange={(e) => updateVariant(index, 'value', e.target.value)}
                        className="w-full px-2 py-1 border border-white/15 rounded  text-sm"
                        placeholder="EU 42"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50  mb-1">SKU</label>
                      <input
                        type="text"
                        value={variant.sku}
                        onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                        className="w-full px-2 py-1 border border-white/15 rounded  text-sm"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50  mb-1">Price (€)</label>
                      <input
                        type="number"
                        value={variant.price}
                        onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1 border border-white/15 rounded  text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50  mb-1">Stock</label>
                      <input
                        type="number"
                        value={variant.quantity}
                        onChange={(e) => updateVariant(index, 'quantity', parseInt(e.target.value) || 0)}
                        min="0"
                        className="w-full px-2 py-1 border border-white/15 rounded  text-sm"
                      />
                    </div>
                  </div>
                  {variants.length > 1 && (
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => deleteVariant(index)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-red-400 hover:bg-red-500/10 rounded  text-sm"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => navigate('/provider/catalog')}
            disabled={saving}
            className="px-6 py-2 border border-white/15 text-white/70 rounded  text-sm hover:bg-white/[0.08] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="px-6 py-2 bg-green-600 text-white rounded  text-sm hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Product'}
          </button>
        </div>
      </div>
    </ProviderLayout>
  );
};

export default NewProduct;
